import { LoggedInUser, LoginRequest, useLogin } from '@api';
import { FormFieldType, Language, LocalStorageItem, NavigationPathKey, TranslationKey } from '@enums';
import { errorAtom, getUserLanguageAtom } from '@state';



import { useState } from 'react';



import { FormFieldItem, generateReadableErrorMessages, getToken, getUserData, setAuthenticationStorage } from 'common';
import i18next from 'i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { decodeToken, useJwt } from 'react-jwt';
import { useNavigate } from 'react-router-dom';



import { getAllCompanies } from '../../../../api/endpoints/company/api';
import { customInputStyle } from './style';


/**
 * Initial data
 */
const initialData: LoginRequest = {
  email: '',
  password: '',
}
export function useLoginModel() {
  const setError = useSetAtom(errorAtom)
  const {t} = useTranslation()
  const [loginData, setLoginData] = useState<LoginRequest>(initialData)
  const [loginDataErrors, setLoginDataErrors] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const CURRENT_STORAGE_VERSION = '1'
  const getUserLanguage = useAtomValue(getUserLanguageAtom)

  const {mutate: loginMutation} = useLogin()

  /**
   * A function to handle change of the login form fields
   * @param event Change event
   */
  const onChangeLoginField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fieldName = event.target.name
    const value = event.target.value
    setLoginDataErrors(prev => prev.filter(item => item !== fieldName))
    setLoginData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Validation function
   * @returns If there are any invalid fields
   */
  const validateData = (): boolean => {
    const invalidValues: string[] = []

    for (const prop in loginData) {
      if (Object.prototype.hasOwnProperty.call(loginData, prop)) {
        const value = loginData[prop as keyof LoginRequest]

        if (prop === 'email') {
          if (typeof value === 'string' && (!value || !/\S+@\S+\.\S+/.test(value))) {
            invalidValues.push(prop)
          }
        } else if (typeof value === 'string' && (value === undefined || value === '')) {
          invalidValues.push(prop)
        }
      }
      setLoginDataErrors(invalidValues)
    }
    if (invalidValues.length > 0)
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })
    return invalidValues.length > 0
  }

  /**
   * Login form fields configuration
   */
  const loginFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Username']),
      name: 'email',
      onChange: onChangeLoginField,
      value: loginData.email,
      error: loginDataErrors.includes('email'),
      customStyle: customInputStyle,
      onKeyDown: (key: React.KeyboardEvent) => {
        if (key.key === 'Enter') onLogin()
      },
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Password']),
      name: 'password',
      onChange: onChangeLoginField,
      value: loginData.password,
      error: loginDataErrors.includes('password'),
      customStyle: customInputStyle,
      type: 'password',
      onKeyDown: (key: React.KeyboardEvent) => {
        if (key.key === 'Enter') onLogin()
      },
    },
  ]

  const onHcsLogin = () => {
    const authUrl = `${process.env.REACT_APP_HCS_AUTH_URL || ''}/authorize`
    const redirect_uri = process.env.REACT_APP_HCS_AUTH_REDIRECT_URI || ''
    const client_id = process.env.REACT_APP_HCS_AUTH_CLIENT_ID || ''

    const state = crypto.randomUUID()
    const nonce = crypto.randomUUID()

    localStorage.setItem('oidc_state', state)
    localStorage.setItem('oidc_nonce', nonce)

    const data = qs.stringify({
      response_type: 'code',
      client_id,
      redirect_uri,
      scope: 'openid email profile',
      state,
      nonce,
    })

    window.location.href = `${authUrl}?${data}`
  }

  const checkAndResetLocalStorage = () => {
    const storedVersion = localStorage.getItem(LocalStorageItem.STORAGE_VERSION)

    if (storedVersion !== CURRENT_STORAGE_VERSION) {
      Object.keys(localStorage).forEach(key => {
        localStorage.removeItem(key)
      })

      localStorage.setItem(LocalStorageItem.STORAGE_VERSION, CURRENT_STORAGE_VERSION)
    }
  }

  const checkLogin = async () => {


    if (!getUserData()) {
      setLoading(false);
      return;
    }
    try {
      await getAllCompanies();
      navigate(NavigationPathKey.DASHBOARD)
    } catch (error) {
      console.error(error)
    }

    setLoading(false)
  }

  /**
   * A function to handle the Login button click. If successfull, will log in the user.
   */
  const onLogin = () => {
    if (validateData()) return

    loginMutation(loginData, {
      onSuccess: (res) => {
        setAuthenticationStorage(
          JSON.stringify({
            userData: {...res.userDetails},
          }),
        )
        setLoading(true);

        i18next.changeLanguage(getUserLanguage(res.userDetails.id));

        navigate(NavigationPathKey.DASHBOARD)
      },
    })
  }

  return {onLogin, onChangeLoginField, t, checkLogin, loginData, loginFormFields, onHcsLogin, loading: loading}
}
